// src/pages/Projects.js
import React, { useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import CaseStudyCard from '../components/CaseStudyCard';
import caseStudiesData from '../data/caseStudies.json';
import caseStudyDetailsData from '../data/caseStudyDetails.json';

const PageWrapper = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 120px 20px ${({ theme }) => theme.spacing['2xl']};
  position: relative;
  z-index: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 100px 20px ${({ theme }) => theme.spacing.xl};
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};
`;

const PageTitle = styled.h1`
  margin: 0 0 ${({ theme }) => theme.spacing.base} 0;
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes['2xl']};
  }
`;

const PageSubtitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.base};
  }
`;

const ActiveFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const FilterTag = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.primary}15;
  color: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.primary}25;
  }
`;

const ResultCount = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  strong {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const StudiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 10px;
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing['3xl']};
  color: ${({ theme }) => theme.colors.textSecondary};

  p {
    font-size: ${({ theme }) => theme.fontSizes.lg};
    margin-bottom: ${({ theme }) => theme.spacing.base};

    &:last-child {
      font-size: ${({ theme }) => theme.fontSizes.base};
      color: ${({ theme }) => theme.colors.textTertiary};
    }
  }
`;

// Helper to extract searchable text from content sections
const getSearchableContent = (content) => {
  if (!content || !Array.isArray(content)) return '';

  return content.map(section => {
    if (!section) return '';

    // Text content (h2, h3, body, p)
    if (typeof section.content === 'string') {
      return section.content;
    }

    // List items
    if (Array.isArray(section.content)) {
      return section.content.join(' ');
    }

    // Image/video captions
    if (section.caption) {
      return section.caption;
    }

    // Code blocks
    if (section.content?.code) {
      return section.content.code;
    }

    return '';
  }).join(' ');
};

const Projects = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Open search modal with current search value
  const openSearchModal = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('openSearch', 'true');
    navigate(`/projects?${newParams.toString()}`);
  };

  // Merge case studies with details to get tags and content
  const allProjects = useMemo(() => {
    return caseStudiesData.caseStudies.map(study => {
      const details = caseStudyDetailsData.caseStudyDetails.find(d => d.id === study.id);
      return {
        ...study,
        tags: details?.tags || {},
        interactionModality: details?.interactionModality || [],
        content: details?.content || [],
        subtitle: details?.subtitle || ''
      };
    });
  }, []);

  // Parse URL params
  const searchQuery = searchParams.get('q') || '';
  // Each derived array is memoized on searchParams so its identity is stable between
  // renders; a bare `... || []` re-created the array every render and made the
  // filteredStudies memo recompute unconditionally (react-hooks/exhaustive-deps).
  const companiesFilter = useMemo(() => searchParams.get('companies')?.split(',').filter(Boolean) || [], [searchParams]);
  const aiToolsFilter = useMemo(() => searchParams.get('aiTools')?.split(',').filter(Boolean) || [], [searchParams]);
  const designToolsFilter = useMemo(() => searchParams.get('designTools')?.split(',').filter(Boolean) || [], [searchParams]);
  const devToolsFilter = useMemo(() => searchParams.get('devTools')?.split(',').filter(Boolean) || [], [searchParams]);
  const rolesFilter = useMemo(() => searchParams.get('roles')?.split(',').filter(Boolean) || [], [searchParams]);
  const skillsFilter = useMemo(() => searchParams.get('skills')?.split(',').filter(Boolean) || [], [searchParams]);

  const hasActiveFilters = searchQuery || companiesFilter.length > 0 ||
    aiToolsFilter.length > 0 || designToolsFilter.length > 0 ||
    devToolsFilter.length > 0 || rolesFilter.length > 0 || skillsFilter.length > 0;

  // Filter projects based on URL params
  const filteredStudies = useMemo(() => {
    return allProjects.filter(study => {
      // Search query filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const contentText = getSearchableContent(study.content).toLowerCase();
        const matchesSearch =
          study.title?.toLowerCase().includes(query) ||
          study.description?.toLowerCase().includes(query) ||
          study.company?.toLowerCase().includes(query) ||
          study.subtitle?.toLowerCase().includes(query) ||
          contentText.includes(query);
        if (!matchesSearch) return false;
      }

      // Company filter
      if (companiesFilter.length > 0) {
        if (!study.company || !companiesFilter.includes(study.company)) {
          return false;
        }
      }

      // AI Tools filter
      if (aiToolsFilter.length > 0) {
        const studyTools = study.tags?.aiTools || [];
        const hasMatch = aiToolsFilter.some(tool => studyTools.includes(tool));
        if (!hasMatch) return false;
      }

      // Design Tools filter
      if (designToolsFilter.length > 0) {
        const studyTools = study.tags?.designTools || [];
        const hasMatch = designToolsFilter.some(tool => studyTools.includes(tool));
        if (!hasMatch) return false;
      }

      // Dev Tools filter
      if (devToolsFilter.length > 0) {
        const studyTools = study.tags?.devTools || [];
        const hasMatch = devToolsFilter.some(tool => studyTools.includes(tool));
        if (!hasMatch) return false;
      }

      // Roles filter
      if (rolesFilter.length > 0) {
        const studyRoles = study.tags?.roles || [];
        const hasMatch = rolesFilter.some(role => studyRoles.includes(role));
        if (!hasMatch) return false;
      }

      // Skills filter
      if (skillsFilter.length > 0) {
        const studySkills = study.tags?.skills || [];
        const hasMatch = skillsFilter.some(skill => studySkills.includes(skill));
        if (!hasMatch) return false;
      }

      return true;
    });
  }, [allProjects, searchQuery, companiesFilter, aiToolsFilter, designToolsFilter, devToolsFilter, rolesFilter, skillsFilter]);

  // Collect all active filter tags for display
  const activeFilterTags = [
    ...companiesFilter,
    ...aiToolsFilter,
    ...designToolsFilter,
    ...devToolsFilter,
    ...rolesFilter,
    ...skillsFilter
  ];

  if (allProjects.length === 0) {
    return (
      <PageWrapper>
        <PageHeader>
          <PageTitle>Projects</PageTitle>
          <PageSubtitle>
            Case studies, experiments, and production-ready solutions
          </PageSubtitle>
        </PageHeader>
        <EmptyState>
          <p>No projects available yet.</p>
          <p>Check back soon for featured work!</p>
        </EmptyState>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <PageHeader>
        <PageTitle>Projects</PageTitle>
        <PageSubtitle>
          Case studies, experiments, and production-ready solutions
        </PageSubtitle>
      </PageHeader>

      {hasActiveFilters && (
        <>
          {(searchQuery || activeFilterTags.length > 0) && (
            <ActiveFilters>
              {searchQuery && <FilterTag onClick={openSearchModal}>Search: "{searchQuery}"</FilterTag>}
              {activeFilterTags.map(tag => (
                <FilterTag key={tag}>{tag}</FilterTag>
              ))}
            </ActiveFilters>
          )}
          <ResultCount>
            Showing <strong>{filteredStudies.length}</strong> of {allProjects.length} projects
          </ResultCount>
        </>
      )}

      {filteredStudies.length > 0 ? (
        <StudiesGrid>
          {filteredStudies.map((study) => (
            <CaseStudyCard key={study.id} study={study} />
          ))}
        </StudiesGrid>
      ) : (
        <EmptyState>
          <p>No projects match your filters.</p>
          <p>Try adjusting your search or filter criteria.</p>
        </EmptyState>
      )}
    </PageWrapper>
  );
};

export default Projects;
